import type { Workitem } from '@rhythmic/db/types'
import type { WorkitemSearchFilters } from '@rhythmic/types'
import type { UseLiveQueryReturn } from '@tanstack/vue-db'
import type { Hookable } from 'hookable'
import type { Ref } from 'vue'

import type { BacklogContextEvents } from '../useBacklogContext.events.schema'

interface BacklogContextChangedLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
}

export interface BacklogContextChangedHandlerParams {
  hooks: Hookable<{
    [K in keyof BacklogContextEvents]: (event: BacklogContextEvents[K]) => void | Promise<void>
  }>
  logger: BacklogContextChangedLoggerLike
  workitemsQuery: Ref<UseLiveQueryReturn<Workitem> | undefined>
  clearBacklogScopedQueries: () => void
  createWorkitemsQuery: (
    teamId: string,
    filters: WorkitemSearchFilters | undefined,
  ) => UseLiveQueryReturn<Workitem> | undefined
  getFilters: () => WorkitemSearchFilters | undefined
  workitemsQueryKey: Ref<string | undefined>
}

function normalizeFiltersKey(filters: WorkitemSearchFilters | undefined): string {
  const statuses = filters?.statuses?.slice().sort().join(',') ?? ''
  const types = filters?.types?.slice().sort().join(',') ?? ''
  return `statuses:${statuses}|types:${types}`
}

/**
 * Registers a hook handler that configures backlog workitems queries when identifiers change.
 *
 * When `teamId` is missing, queries are cleared. When a team is available, the
 * handler creates (or reuses) the workitems query, deduping by `teamId+filters`.
 */
export default function useBacklogContextOnContextChanged({
  hooks,
  logger,
  workitemsQuery,
  clearBacklogScopedQueries,
  createWorkitemsQuery,
  getFilters,
  workitemsQueryKey,
}: BacklogContextChangedHandlerParams) {
  return hooks.hook('ContextChanged', (event) => {
    if (!event.teamId) {
      workitemsQueryKey.value = undefined
      clearBacklogScopedQueries()
      return
    }

    const filters = getFilters()
    const nextKey = `${event.teamId}:${normalizeFiltersKey(filters)}`
    if (workitemsQueryKey.value === nextKey && workitemsQuery.value) return

    workitemsQueryKey.value = nextKey
    workitemsQuery.value = createWorkitemsQuery(event.teamId, filters)
  })
}
